import { useState } from 'react';
import { Button, Card, EmptyState, Modal, Pill } from '@salonomy/ui';
import { useAppointments } from '../data/appointments.js';
import { useSalon } from '../data/salon.js';
import { useServices } from '../data/services.js';
import {
  NoShowChargeModal,
  PaymentsLedger,
  ReceiptHistory,
  type NoShowApptContext,
} from '../features/payments/index.js';
import { useAuth } from '../lib/auth.js';
import { money } from '../lib/format.js';
import { clockLabelAt, todayInZone } from '../lib/time.js';

/**
 * WS6 — the payments/receipts back-office route (manager-only in nav). Composes the takings ledger and
 * the receipt-history surfaces as stacked Cards (mirroring ReportsPage), plus a "Charge no-show fee"
 * entry point that lists today's no-show appointments for the manager to charge against.
 *
 * MONEY HONESTY: this page composes; every figure shown by the children is a server DTO figure. The
 * no-show path MINTS a charge (deposit FORFEIT semantics), the ledger's per-row Refund REFUNDS — the two
 * are kept visually and structurally separate so money never moves the wrong way.
 */
export function PaymentsPage() {
  const { isManager } = useAuth();
  const { data: salon } = useSalon();
  const tz = salon?.timezone ?? 'Europe/London';
  const today = todayInZone(tz);
  const { data: appts = [] } = useAppointments(today, salon?.timezone);
  const { data: services } = useServices();

  const [noShowPicker, setNoShowPicker] = useState(false);
  const [charging, setCharging] = useState<NoShowApptContext | null>(null);

  // Only appointments the floor has marked no_show are chargeable (the API 409s otherwise).
  const noShows = appts.filter((a) => a.status === 'no_show');

  return (
    <>
      <div className="topbar">
        <div className="topbar__left">
          <div className="topbar__title">Payments</div>
          <span className="muted">Refunds, receipts &amp; no-show fees</span>
        </div>
        {isManager ? (
          <div className="topbar__right">
            <Button variant="outline" size="sm" onClick={() => setNoShowPicker(true)}>
              Charge no-show fee
            </Button>
          </div>
        ) : null}
      </div>

      <div className="screen-scroll">
        <div
          className="screen-pad"
          style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1040 }}
        >
          <PaymentsLedger />
          <ReceiptHistory />

          <Card>
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
              Every amount on this screen is the payments server&rsquo;s — refundable balances, VAT
              and fees are never recomputed here. A refund returns money on an existing payment; a
              no-show fee mints a new charge against a no-show appointment (a deposit forfeit is the
              no-show path, not a refund). Refunds, voids and no-show charges are manager actions.
            </div>
          </Card>
        </div>
      </div>

      {noShowPicker ? (
        <Modal
          open
          onClose={() => setNoShowPicker(false)}
          title="Charge a no-show fee"
          width={460}
          footer={
            <Button variant="outline" onClick={() => setNoShowPicker(false)}>
              Close
            </Button>
          }
        >
          <div className="muted" style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
            Pick a no-show from today. The appointment must already be marked{' '}
            <strong>no-show</strong> in the diary — late-cancel charging has no public route yet, so
            this is no-show-only.
          </div>
          {noShows.length === 0 ? (
            <EmptyState
              title="No no-shows today"
              description="Mark an appointment no-show in the diary first, then charge its fee here."
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {noShows.map((a) => (
                <button
                  key={a.id}
                  className="till-row"
                  onClick={() => {
                    setCharging({
                      appointmentId: a.id,
                      serviceId: a.serviceId,
                      clientName: a.clientDisplayName || 'Walk-in',
                      priceQuotedPence: a.priceQuoted,
                      isNoShow: a.status === 'no_show',
                    });
                    setNoShowPicker(false);
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="client-row__name">{a.clientDisplayName || 'Walk-in'}</div>
                    <div className="client-row__sub">
                      {clockLabelAt(a.startAt, tz)} ·{' '}
                      {services?.find((s) => s.id === a.serviceId)?.name ?? 'Service'}
                    </div>
                  </div>
                  <Pill tone="neutral" className="mono">
                    {money(a.priceQuoted)}
                  </Pill>
                </button>
              ))}
            </div>
          )}
        </Modal>
      ) : null}

      {charging ? <NoShowChargeModal appt={charging} onClose={() => setCharging(null)} /> : null}
    </>
  );
}
